import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Pencil, Trash2, FileText, Eye, EyeOff, X, Save, ExternalLink } from 'lucide-react';

const PageManager = () => {
  const { api } = useAuth();
  const [pages, setPages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', title: '', content: '', meta_title: '', meta_description: '', published: true });

  const load = () => api.get('/admin/pages').then(r => setPages(r.data || [])).catch(console.error);
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ slug: '', title: '', content: '', meta_title: '', meta_description: '', published: true }); setEditing(null); setShowForm(false); };

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/pages/${editing}`, form); }
      else { await api.post('/admin/pages', form); }
      resetForm(); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (p) => {
    setForm({ slug: p.slug, title: p.title, content: p.content || '', meta_title: p.meta_title || '', meta_description: p.meta_description || '', published: p.published !== false });
    setEditing(p.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    await api.delete(`/admin/pages/${id}`); load();
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><FileText size={20} /> Pages ({pages.length})</h2>
        <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}><Plus size={16} /> Add Page</button>
      </div>

      {showForm && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="card-subtitle" style={{ margin: 0 }}>{editing ? 'Edit Page' : 'Create New Page'}</h3>
            <button className="btn-sm btn-delete" onClick={resetForm}><X size={14} /></button>
          </div>
          <form onSubmit={handleSave}>
            <div className="two-col">
              <div>
                <label className="form-label">Page Title *</label>
                <input className="form-input" value={form.title} onChange={e => {
                  setForm({...form, title: e.target.value, slug: editing ? form.slug : autoSlug(e.target.value) });
                }} required placeholder="e.g. About Us, Terms, Privacy Policy" />
              </div>
              <div>
                <label className="form-label">URL Slug *</label>
                <input className="form-input" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} required placeholder="e.g. about-us" />
                <span style={{ fontSize: 10, color: '#555', marginTop: 4, display: 'block' }}>URL: raja-bokep.com/pages/{form.slug || 'your-slug'}</span>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="form-label">Page Content (supports HTML)</label>
              <textarea className="form-textarea" value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={12} required
                placeholder={'Tulis content halaman di sini...\n\nBisa pakai HTML:\n<h2>Heading</h2>\n<p>Paragraf...</p>\n<a href="https://...">Link</a>'} 
                style={{ minHeight: 250, fontFamily: "'Courier New', monospace", fontSize: 13 }} />
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="form-label" style={{ fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 8 }}>SEO Settings</label>
              <div className="two-col">
                <div>
                  <label className="form-label">Meta Title</label>
                  <input className="form-input" value={form.meta_title} onChange={e => setForm({...form, meta_title: e.target.value})} placeholder="SEO title for search engines" />
                </div>
                <div>
                  <label className="form-label">Meta Description</label>
                  <input className="form-input" value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})} placeholder="Brief description for search results" />
                </div>
              </div>
            </div>

            <div className="toggle-wrap" style={{ marginTop: 12 }}>
              <button type="button" className={`toggle-switch ${form.published ? 'active' : ''}`} onClick={() => setForm({...form, published: !form.published})} />
              <span className="toggle-label">Published (Visible on website)</span>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="submit" className="btn-primary"><Save size={16} /> {editing ? 'Update Page' : 'Create Page'}</button>
              <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr><th>Title</th><th>Slug</th><th>Status</th><th>Created</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#555' }}>No pages yet. Create About, Terms, Privacy pages!</td></tr>
            ) : pages.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 700, color: '#ddd' }}>{p.title}</td>
                <td style={{ color: '#f59e0b', fontSize: 12 }}>/pages/{p.slug}</td>
                <td>
                  {p.published !== false ? (
                    <span className="badge badge-success"><Eye size={10} /> Published</span>
                  ) : (
                    <span className="badge badge-danger"><EyeOff size={10} /> Draft</span>
                  )}
                </td>
                <td style={{ fontSize: 11, color: '#555' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-sm btn-edit" onClick={() => handleEdit(p)}><Pencil size={12} /></button>
                    <button className="btn-sm btn-delete" onClick={() => handleDelete(p.id)}><Trash2 size={12} /></button>
                    {p.published !== false && (
                      <a href={`https://raja-bokep.com/pages/${p.slug}`} target="_blank" rel="noreferrer" className="btn-sm btn-edit"><ExternalLink size={12} /></a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PageManager;
